import type { AxiosRequestConfig } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import i18n from "@/lib/i18n";
import { authService } from "./auth.service";
import {
  LoginCredentials,
  SendOtpRequest,
  VerifyOtpRequest,
  SaveTempUserRequest,

  ForgotPasswordRequest,
  ResetPasswordRequest,
  UpdatePasswordRequest,
  RecoverUserIdRequest,
  UpdateUserRequest,
  OtpResponse,
} from "../types";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getStoreState } from "@/stores";
import { clearCacheOnLogout } from "./cache";
import { identifyUser, resetUser } from "@/shared/posthog/identify";
import { captureEvent } from "@/shared/posthog/analytics";
import { PostHogEvents } from "@/shared/posthog/events";
import { MUST_COMPLETE_RESET_STATUSES } from "../types";

// In-memory holder so the temp password survives any redirect path
// (GuestGuard, AuthGuard, or forceChangePassword navigate).
let _loginTempPassword: string | null = null;
export function setLoginTempPassword(pw: string) { _loginTempPassword = pw; }
export function consumeLoginTempPassword(): string | null {
  const pw = _loginTempPassword;
  _loginTempPassword = null;
  return pw;
}

// ============================================
// Query Keys
// ============================================

export const authKeys = {
  all: ["auth"] as const,
  user: () => [...authKeys.all, "user"] as const,
  sessionCheck: () => [...authKeys.all, "sessionCheck"] as const,
  countries: () => [...authKeys.all, "countries"] as const,
  passportTypes: () => [...authKeys.all, "passportTypes"] as const,
  signupConfig: () => [...authKeys.all, "signupConfig"] as const,
};

// ============================================
// Public Data Hooks
// ============================================

export const useCountries = () => {
  return useQuery({
    queryKey: authKeys.countries(),
    queryFn: authService.getCountries,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const usePassportTypes = () => {
  return useQuery({
    queryKey: authKeys.passportTypes(),
    queryFn: authService.getPassportTypes,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useSignupConfig = () => {
  return useQuery({
    queryKey: authKeys.signupConfig(),
    queryFn: authService.getSignupConfig,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// ============================================
// Session & Auth Hooks
// ============================================

/** Lightweight session check – use on app load. GET /auth/session */
export const useSessionCheck = () => {
  return useQuery({
    queryKey: authKeys.sessionCheck(),
    queryFn: authService.getSessionCheck,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    gcTime: Infinity,
    placeholderData: (previousData) => previousData,
  });
};

/** Full user + session metadata – use only when on protected route. GET /users/me */
export const useUser = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: authKeys.user(),
    queryFn: authService.getSession,
    enabled: options?.enabled !== false,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    gcTime: Infinity,
    placeholderData: (previousData) => previousData,
  });
};

export interface UseLoginOptions {
  redirectTo?: string;
  /** Called when BE returns 403 with Retry-After (login locked). */
  onLoginLocked?: (retryAfterSeconds: number) => void;
}

export const useLogin = (options?: UseLoginOptions) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const redirectTo = options?.redirectTo || "/my-account";

  return useMutation({
    mutationFn: ({ creds, config }: { creds: LoginCredentials; config?: AxiosRequestConfig }) =>
      authService.login(creds, config),
    onSuccess: async (data, variables) => {
      queryClient.setQueryData(authKeys.user(), data.user);
      // Refetch session check so cache reflects server state (no fake cache); AuthProvider will then see authenticated
      try {
        await queryClient.refetchQueries({ queryKey: authKeys.sessionCheck() });
      } catch {
        // Proceed anyway so login is not blocked if session check fails (e.g. network)
      }
      getStoreState().setAuth({
        user: data.user,
        isAuthenticated: true,
        accountStatus: data.user?.status ?? null,
        isAuthLoading: false,
      });

      const userStatus = data.user?.status ?? null;
      if (userStatus && MUST_COMPLETE_RESET_STATUSES.includes(userStatus)) {
        setLoginTempPassword(variables.creds.password);
      }

      const fromRegistration =
        typeof sessionStorage !== "undefined" &&
        sessionStorage.getItem("bls_from_registration") === "1";
      if (fromRegistration) {
        sessionStorage.removeItem("bls_from_registration");
        sessionStorage.setItem("bls_first_my_account_visit", "1");
      }

      if (data.user) {
        const created = data.user.createdAt instanceof Date
          ? data.user.createdAt.toISOString()
          : String(data.user.createdAt ?? "");
        identifyUser(data.user.id, {
          email: data.user.email,
          ...(created && { registration_date: created }),
        });
      }

      captureEvent(PostHogEvents.LOGIN_SUCCEEDED, {
        auth_method: "password",
        is_first_login: fromRegistration,
      });
      if (fromRegistration) {
        captureEvent(PostHogEvents.FIRST_SIGNIN_SUCCEEDED, {
          auth_method: "password",
          is_first_login: true,
        });
      }

      if (data.forceChangePassword === true) {
        navigate("/reset-password", { replace: true });
        return;
      }

      queryClient.invalidateQueries({ queryKey: authKeys.user() });
      navigate(redirectTo);
      toast.success(i18n.t('toasts.welcomeBack', { ns: 'common' }));
    },
    onError: (error: any) => {
      const status = error?.response?.status ?? error?.status;
      const headers = error?.response?.headers ?? {};
      const retryAfter = headers['retry-after'] ?? headers['Retry-After'];

      // 403 + Retry-After: account locked — show lockout UI and toast
      if (status === 403 && retryAfter != null) {
        const sec = parseInt(String(retryAfter).trim(), 10);
        if (Number.isFinite(sec) && sec > 0) {
          options?.onLoginLocked?.(sec);
          const minutes = Math.ceil(sec / 60);
          toast.error(i18n.t('auth:loginLock.message', { minutes }));
          return;
        }
      }

      const msg = error?.response?.data?.message ?? error?.message;
      captureEvent(PostHogEvents.LOGIN_FAILED, {
        reason: typeof msg === "string" ? msg : "login_failed",
      });

      toast.error(error?.message || i18n.t('toasts.loginFailed', { ns: 'auth' }));
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      clearCacheOnLogout(queryClient, {
        sessionCheckKey: authKeys.sessionCheck(),
        userKey: authKeys.user(),
      });
      getStoreState().clearAuth();
      captureEvent(PostHogEvents.LOGOUT);
      resetUser();
      navigate("/");
    },
  });
};

export const useRefreshSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authService.refreshSession,
    onSuccess: (data) => {
      queryClient.setQueryData(authKeys.user(), (oldUser: any) => {
        if (!oldUser) return null;
        return {
          ...oldUser,
          session: {
            ...oldUser.session,
            lastAccessedAt: data.lastAccessedAt,
            ...(data.absoluteExpiresAt != null && { absoluteExpiresAt: data.absoluteExpiresAt }),
          },
        };
      });
    },
  });
};

// ============================================
// Signup Flow Hooks
// ============================================

/** Save temporary user data before email verification */
export const useSaveTempUser = () => {
  return useMutation({
    mutationFn: ({ data, config }: { data: SaveTempUserRequest; config?: AxiosRequestConfig }) =>
      authService.saveTempUser(data, config),
    onError: (error: any) => {
      toast.error(error.message || i18n.t('toasts.saveTempUserFailed', { ns: 'auth' }));
    },
  });
};

/** Send OTP to email */
export const useSendOtp = () => {
  return useMutation({
    mutationFn: ({ data, config }: { data: SendOtpRequest; config?: AxiosRequestConfig }) =>
      authService.sendOtp(data, config),
    onSuccess: (data: OtpResponse) => {
      toast.success(data.message || i18n.t('toasts.otpSent', { ns: 'common' }));
    },
    onError: (error: any) => {
      toast.error(error.message || i18n.t('toasts.sendOtpFailed', { ns: 'auth' }));
    },
  });
};

/** Verify OTP */
export const useVerifyOtp = () => {
  return useMutation({
    mutationFn: ({ data, config }: { data: VerifyOtpRequest; config?: AxiosRequestConfig }) =>
      authService.verifyOtp(data, config),
    onError: (error: any) => {
      toast.error(error.message || i18n.t('toasts.invalidOtp', { ns: 'auth' }));
    },
  });
};



// ============================================
// Password Reset Flow Hooks
// ============================================

/** Initiate forgot password flow */
export const useForgotPassword = () => {
  return useMutation({
    mutationFn: ({ data, config }: { data: ForgotPasswordRequest; config?: AxiosRequestConfig }) =>
      authService.forgotPassword(data, config),
    onError: (error: any) => {
      toast.error(error.message || i18n.t('toasts.forgotPasswordFailed', { ns: 'auth' }));
    },
  });
};

/** Reset password (after forgot password OTP verification) */
export const useResetPassword = () => {
  return useMutation({
    mutationFn: (data: ResetPasswordRequest) => authService.resetPassword(data),
    onSuccess: () => {
      toast.success(i18n.t('toasts.passwordReset', { ns: 'common' }));
    },
    onError: (error: any) => {
      toast.error(error.message || i18n.t('toasts.resetPasswordFailed', { ns: 'auth' }));
    },
  });
};

/** Update password (first login or authenticated change) */
export const useUpdatePassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdatePasswordRequest) => authService.updatePassword(data),
    onSuccess: () => {
      // Invalidate user query to refetch user data with updated status
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
    },
    onError: (error: any) => {
      toast.error(error.message || i18n.t('toasts.updatePasswordFailed', { ns: 'auth' }));
    },
  });
};



// ============================================
// Account Management Hooks
// ============================================

/** Update user profile. On success, refetches GET /me so the UI stays in sync with the source of truth. */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateUserRequest) => authService.updateUser(data),
    onSuccess: async () => {
      toast.success(i18n.t('toasts.profileUpdated', { ns: 'common' }));
      // Refetch user from GET /me so UI (and store via AuthProvider) reflect server state
      await queryClient.refetchQueries({ queryKey: authKeys.user() });
    },
    onError: (error: any) => {
      toast.error(error.message || i18n.t('toasts.updateProfileFailed', { ns: 'auth' }));
    },
  });
};

/** Delete user account (soft delete) */
export const useDeleteAccount = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => authService.deleteAccount(),
    onSuccess: () => {
      clearCacheOnLogout(queryClient, {
        sessionCheckKey: authKeys.sessionCheck(),
        userKey: authKeys.user(),
      });
      getStoreState().clearAuth();
      captureEvent(PostHogEvents.ACCOUNT_DELETED);
      captureEvent(PostHogEvents.LOGOUT);
      resetUser();
      toast.success(i18n.t('toasts.accountDeleted', { ns: 'common' }));
      navigate("/");
    },
    onError: (error: any) => {
      toast.error(error.message || i18n.t('toasts.deleteAccountFailed', { ns: 'auth' }));
    },
  });
};

/** Recover User ID */
export const useRecoverUserId = () => {
  return useMutation({
    mutationFn: ({ data, config }: { data: RecoverUserIdRequest; config?: AxiosRequestConfig }) =>
      authService.recoverUserId(data, config),
    onError: () => {
      // Don't show generic error toast immediately as the component might want to handle it (or show it)
      // toast.error(error.message || "Failed to recover user ID"); 
    },
  });
};

// ============================================
// Legacy Hooks (backward compatibility)
// ============================================

export const useChangePassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { email: string; currentPassword: string; newPassword: string }) => {
      return authService.changePassword(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.user() });
      toast.success(i18n.t('toasts.passwordChanged', { ns: 'common' }));
    },
    onError: (error: any) => {
      toast.error(error.message || i18n.t('toasts.changePasswordFailed', { ns: 'auth' }));
    },
  });
};

